import React, { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { match as Match, useHistory } from "react-router-dom";

import { SaleAction, setActiveRole, setCurrentLivestockSaleId } from "actions";

import { SlimButton } from "components/Button";
import { CenteredGreedy, FullscreenContainer, Row } from "components/Layout";
import { FullScreenLoadingSpinner } from "components/LoadingSpinner";
import {
  AgrinousSupportEmailLink,
  AgrinousSupportPhoneLink,
} from "components/Support";
import { BigText } from "components/Text";

import { colors } from "constants/theme";

import { getReloadActionFromSlug } from "lib/auth";
import { getDashboardRoute } from "lib/navigation";

import { getIsFetchingRoles } from "selectors/loading";
import { getRoleBySlug, getSaleById } from "selectors";

import AgriNousIcon from "img/AgriNous-icon";

interface Params {
  livestockSaleId: string;
  userRole: string;
}

interface NotFoundProps {
  missingDataVariable: string;
}

const NotFoundInfoComponent = ({ missingDataVariable }: NotFoundProps) => {
  const history = useHistory();
  const goToDashboard = () => {
    history.push(getDashboardRoute());
  };
  return (
    <FullscreenContainer>
      <CenteredGreedy>
        <Row>
          <AgriNousIcon color={colors.primary} scale={2} size={25} />
        </Row>
        <br />
        <BigText>Not Found</BigText>
        <p>You do not have access to this {missingDataVariable}.</p>
        <Row>
          <SlimButton onClick={goToDashboard}>Go Back</SlimButton>
        </Row>
        <br />
        <p className="text-center p-2">
          For further support, contact us via email at{" "}
          <AgrinousSupportEmailLink />
          <br /> or call us on <AgrinousSupportPhoneLink />
        </p>
      </CenteredGreedy>
    </FullscreenContainer>
  );
};

interface RedirectToSaleProps {
  match: Match<Params>;
}

// Entry point for "king link" deep links: /redir/<saleId>/<roleSlug>/
//
// Flow:
//   1. Check if the given role is in state already.
//     1.1 - If not, fetch roles for the given role slug
//       (getReloadActionFromSlug determines which role type to load —
//        saleyard admin, livestock agent, etc.)
//     1.2. Once roles are loaded, if the role is still not present,
//       we can't progress, tell the user
//   2  setActiveRole is dispatched so the rest of the app knows the active role.
//   3. Once the role is present, check the state for the sale, and fetch
//        if not present. Attempting the sale fetch without a role returns a
//        404 from the API.
//   4. Once the sale is in Redux, setCurrentLivestockSaleId triggers navigation
//      to the sale home screen.
export const RedirectToSale = ({ match }: RedirectToSaleProps) => {
  const { livestockSaleId, userRole } = match.params;
  const role = useSelector(getRoleBySlug(userRole));
  const isFetchingRoles = useSelector(getIsFetchingRoles);
  const dispatch = useDispatch();
  const sale = useSelector(getSaleById(+livestockSaleId));
  const hasRequestedSale = useRef(false);
  const hasRequestedRole = useRef(false);

  // Step 1: fetch roles, if given role is unavailable, on mount.
  useEffect(() => {
    if (hasRequestedRole.current || Boolean(role)) {
      return;
    }
    hasRequestedRole.current = true;
    const roleAction = getReloadActionFromSlug(userRole);
    if (roleAction) {
      dispatch(roleAction());
    }
  }, [dispatch, userRole, role]);

  // Step 2: activate role as soon as it appears in Redux
  useEffect(() => {
    if (role) {
      dispatch(setActiveRole(role));
    }
  }, [dispatch, role]);

  // Step 3 & 4: fetch sale once role is ready, then navigate when confirmed available
  useEffect(() => {
    if (!role) {
      return;
    }
    if (sale) {
      dispatch(setCurrentLivestockSaleId(sale.livestocksale_id, true, false));
    } else if (!hasRequestedSale.current) {
      // Attempt to fetch the sale, once, if not present in state
      hasRequestedSale.current = true;
      dispatch(SaleAction.requestOne({ id: +livestockSaleId }));
    }
  }, [dispatch, sale, role, livestockSaleId]);

  if (!role && !isFetchingRoles && hasRequestedRole.current) {
    return <NotFoundInfoComponent missingDataVariable="User Role" />;
  }

  const loadingSubject = !role ? "role" : "sale";
  return (
    <FullScreenLoadingSpinner
      actionName="Loading"
      subjectName={loadingSubject}
    />
  );
};
